import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FaTrash, FaPlus, FaMinus } from "react-icons/fa";

import FuelLoading from "../../pages/FuelLoading";
import "./CartDrawer.scss";

import { toggleCartDrawer } from "../../features/api/cartSlice";

import {
  useGetCartQuery,
  useUpdateItemQuantityMutation,
  useRemoveItemFromCartMutation,
} from "../../features/api/cartApiSlice";

import {
  updateGuestCartQuantity,
  removeGuestCartItem,
} from "../../utils/guestCart";
import useGuestCart from "../../hook/useGuestCart";

export default function CartDrawer() {
  const dispatch = useDispatch();

  // Authentication
  const { accessToken, authChecked } = useSelector((state) => state.auth);

  const isAuthenticated = Boolean(accessToken);

  // Registered customer cart
  const {
    data: cartData,
    isLoading: isCartLoading,
    isFetching: isCartFetching,
  } = useGetCartQuery(undefined, {
    skip: !authChecked || !isAuthenticated,
  });

  // Guest customer cart
  const guestCart = useGuestCart();

  const [updateItemQuantity] = useUpdateItemQuantityMutation();
  const [removeItemFromCart] = useRemoveItemFromCartMutation();

  const [pendingItem, setPendingItem] = useState(null);
  const [cartError, setCartError] = useState("");

  // Select the appropriate cart
  const cartItems = isAuthenticated ? cartData?.items || [] : guestCart.items;

  const isCartBusy =
    !authChecked || (isAuthenticated && (isCartLoading || isCartFetching));

  const cartTotalPrice = cartItems.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0
  );

  const totalItemsCount = cartItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const itemPayload = (item) => ({
    productId: item.productId,
    variantId: item.variantId || null,
    size: item.size || "",
    color: item.color || "",
    volume: item.volume || "",
  });

  const getItemKey = (item) =>
    String(
      item._id ||
        item.variantId ||
        `${item.productId}-${item.size}-${item.color}-${item.volume}`
    );

  const changeQuantity = async (item, quantity) => {
    setCartError("");

    const key = getItemKey(item);
    setPendingItem(key);

    try {
      if (isAuthenticated) {
        // Existing MongoDB cart
        await updateItemQuantity({
          ...itemPayload(item),
          quantity,
        }).unwrap();
      } else {
        // New guest cart
        updateGuestCartQuantity(itemPayload(item), quantity);
      }
    } catch (error) {
      setCartError(
        error?.data?.message || error?.message || "Unable to change quantity."
      );
    } finally {
      setPendingItem(null);
    }
  };

  const removeItem = async (item) => {
    setCartError("");

    const key = getItemKey(item);
    setPendingItem(key);

    try {
      if (isAuthenticated) {
        // Existing MongoDB cart
        await removeItemFromCart(itemPayload(item)).unwrap();
      } else {
        // New guest cart
        removeGuestCartItem(itemPayload(item));
      }
    } catch (error) {
      setCartError(
        error?.data?.message || error?.message || "Unable to remove item."
      );
    } finally {
      setPendingItem(null);
    }
  };

  return (
    <div className="cart-drawer-overlay">
      <div
        className="cart-drawer-backdrop"
        onClick={() => dispatch(toggleCartDrawer())}
      />

      <div className="cart-drawer-panel">
        <header className="cart-drawer-header">
          <h2>Your Basket ({isCartBusy ? "..." : totalItemsCount})</h2>

          <button
            type="button"
            className="close-drawer-btn"
            onClick={() => dispatch(toggleCartDrawer())}
            aria-label="Close cart"
          >
            ✕
          </button>
        </header>

        {cartError && (
          <p role="alert" style={{ color: "#ef4444", padding: "0 1rem" }}>
            {cartError}
          </p>
        )}

        <div className="cart-drawer-scroll-track">
          {isCartBusy ? (
            <div style={{ padding: "4rem 0" }}>
              <FuelLoading text="Loading your road box..." />
            </div>
          ) : cartItems.length === 0 ? (
            <div className="empty-basket-slate">
              <span className="basket-slate-icon">🛒</span>

              <p>Your road trip basket is currently empty.</p>

              <button
                type="button"
                className="fuel-primary-btn"
                onClick={() => dispatch(toggleCartDrawer())}
                style={{ marginTop: "1rem" }}
              >
                Explore Catalog
              </button>
            </div>
          ) : (
            cartItems.map((item) => {
              const itemKey = getItemKey(item);
              const busy = pendingItem === itemKey;
              const dynamicOptions = item.selectedOptions || {};

              return (
                <div key={itemKey} className="cart-item-row-card">
                  <div className="item-thumb-wrapper">
                    <img src={item.image} alt={item.name} />
                  </div>

                  <div className="item-meta-details">
                    <h4>{item.name}</h4>

                    <div className="item-specs-line">
                      {item.fulfillmentMethod === "printify" &&
                      Object.keys(dynamicOptions).length ? (
                        Object.entries(dynamicOptions).map(([name, value]) => (
                          <span key={name}>
                            {name}: <strong>{value}</strong>
                          </span>
                        ))
                      ) : (
                        <>
                          {item.color && (
                            <span>
                              Color: <strong>{item.color}</strong>
                            </span>
                          )}

                          {item.size && (
                            <span>
                              Size: <strong>{item.size}</strong>
                            </span>
                          )}

                          {item.volume && (
                            <span>
                              Vol: <strong>{item.volume}</strong>
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <div className="item-qty-operator-bar">
                      <button
                        type="button"
                        className="qty-btn"
                        disabled={busy || item.quantity <= 1}
                        onClick={() => changeQuantity(item, item.quantity - 1)}
                        aria-label={`Decrease quantity of ${item.name}`}
                      >
                        <FaMinus size={10} />
                      </button>

                      <span className="qty-display-num">{item.quantity}</span>

                      <button
                        type="button"
                        className="qty-btn"
                        disabled={busy || item.quantity >= 100}
                        onClick={() => changeQuantity(item, item.quantity + 1)}
                        aria-label={`Increase quantity of ${item.name}`}
                      >
                        <FaPlus size={10} />
                      </button>
                    </div>
                  </div>

                  <div className="item-price-purge-column">
                    <span className="item-line-total-price">
                      $
                      {(
                        Number(item.price || 0) * Number(item.quantity || 0)
                      ).toFixed(2)}
                    </span>

                    <button
                      type="button"
                      className="remove-line-item-trash-btn"
                      disabled={busy}
                      onClick={() => removeItem(item)}
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!isCartBusy && cartItems.length > 0 && (
          <footer className="cart-drawer-footer">
            <div className="footer-subtotal-row">
              <span>Subtotal:</span>

              <strong>${cartTotalPrice.toFixed(2)}</strong>
            </div>

            <Link
              to="/checkout"
              className="fuel-primary-btn checkout-cta-btn-link"
              onClick={() => dispatch(toggleCartDrawer())}
            >
              Proceed To Checkout 🚀
            </Link>
          </footer>
        )}
      </div>
    </div>
  );
}
